import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CustomIcon from '../../components/CustomIcon';
import SEOHead from '../../components/SEO/SEOHead';
import { BudgetProvider, useBudget } from './context/BudgetContext';
import { registerChartComponents } from './utils/chartSetup';
import SetupWizard from './components/wizard/SetupWizard';
import DashboardOverview from './components/dashboard/DashboardOverview';
import MainVisualCard from './components/charts/MainVisualCard';
import BudgetBreakdownEditor from './components/breakdown/BudgetBreakdownEditor';
import SmartInsights from './components/insights/SmartInsights';
import GoalsModule from './components/goals/GoalsModule';
import DebtPlanner from './components/debt/DebtPlanner';
import SaveExportBar from './components/export/SaveExportBar';
import ToolHero from './components/hero/ToolHero';

registerChartComponents();

function AutoSaveBadge() {
  const { t } = useTranslation();
  const { lastSavedAt } = useBudget();

  if (!lastSavedAt) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
      <Chip
        icon={<CloudDoneIcon sx={{ fontSize: 14 }} />}
        label={t('budgetPlanner.autoSave', 'Guardado automáticamente')}
        size="small"
        variant="outlined"
        sx={{
          fontSize: '0.7rem',
          height: 24,
          color: '#2E7D32',
          borderColor: '#A5D6A7',
          bgcolor: '#E8F5E9',
          '& .MuiChip-icon': { color: '#2E7D32' },
        }}
      />
    </Box>
  );
}

function AnalyzingLoader({ onDone }) {
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(onDone, 1500);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        gap: 2,
      }}
    >
      <CircularProgress size={48} sx={{ color: '#1E3A5F' }} />
      <Typography variant="h6" fontWeight={500} sx={{ color: '#1E3A5F' }}>
        {t('budgetPlanner.wizard.analyzing', 'Analizando tus finanzas...')}
      </Typography>
    </Box>
  );
}

function TransitionCTA() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || 'es';

  const links = [
    { label: t('budgetPlanner.cta.cards', 'Comparar tarjetas'), path: `/${lng}/tarjetas-de-credito`, icon: <CustomIcon name="creditCard" size={20} sx={{ filter: 'brightness(0) invert(1)' }} /> },
    { label: t('budgetPlanner.cta.savings', 'Cuentas de ahorro'), path: `/${lng}/banca`, icon: <CustomIcon name="piggyBank" size={20} sx={{ filter: 'brightness(0) invert(1)' }} /> },
    { label: t('budgetPlanner.cta.mortgages', 'Hipotecas'), path: `/${lng}/hipotecas`, icon: <CustomIcon name="house" size={20} sx={{ filter: 'brightness(0) invert(1)' }} /> },
  ];

  return (
    <Box
      sx={{
        mt: 2,
        py: { xs: 3, md: 5 },
        px: 3,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #1A1A1A 0%, #1E3A5F 100%)',
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" fontWeight={600} sx={{ color: '#fff', mb: 1 }}>
        {t('budgetPlanner.cta.title', '¿Listo para optimizar?')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, maxWidth: 480, mx: 'auto' }}>
        {t('budgetPlanner.cta.subtitle', 'Explora nuestras herramientas de comparación para sacar el máximo provecho de tu presupuesto.')}
      </Typography>
      <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 600, mx: 'auto' }}>
        {links.map((link) => (
          <Grid item xs={12} sm={4} key={link.path}>
            <Button
              component={RouterLink}
              to={link.path}
              variant="outlined"
              startIcon={link.icon}
              fullWidth
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {link.label}
            </Button>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function SectionLabel({ text }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#5F6B7A',
        mb: 1,
      }}
    >
      {text}
    </Typography>
  );
}

function BudgetPlannerContent() {
  const { t } = useTranslation();
  const { state } = useBudget();
  const showDashboard = state.wizardCompleted;

  // Track if we just completed the wizard (for micro-loader)
  const [analyzing, setAnalyzing] = useState(false);
  const wasCompleted = useRef(state.wizardCompleted);

  useEffect(() => {
    // Show loader only when transitioning from wizard to dashboard
    if (state.wizardCompleted && !wasCompleted.current) {
      setAnalyzing(true);
    }
    wasCompleted.current = state.wizardCompleted;
  }, [state.wizardCompleted]);

  return (
    <>
      <SEOHead
        title={t('budgetPlanner.seo.title', 'Planificador de Presupuesto')}
        description={t('budgetPlanner.seo.description', 'Organiza tus finanzas en Canadá con nuestro planificador de presupuesto gratuito. Controla ingresos, gastos y metas de ahorro.')}
        canonical="/budget-planner"
        routeKey="budgetPlanner"
      />
      <ToolHero />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        {analyzing ? (
          <AnalyzingLoader onDone={() => setAnalyzing(false)} />
        ) : !showDashboard ? (
          <SetupWizard />
        ) : (
          <Box
            id="budget-dashboard"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.5, md: 2.5 },
              opacity: 1,
              animation: 'fadeInUp 0.5s ease-out',
              '@keyframes fadeInUp': {
                from: { opacity: 0, transform: 'translateY(12px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <AutoSaveBadge />
            <SectionLabel text="Tu resumen" />
            <DashboardOverview />
            <Divider sx={{ my: 1, borderColor: '#EEF0F3' }} />
            <SectionLabel text="Distribución" />
            <MainVisualCard />
            <SectionLabel text="Qué puedes hacer" />
            <SmartInsights />
            <Divider sx={{ my: 1, borderColor: '#EEF0F3' }} />
            <BudgetBreakdownEditor />
            <GoalsModule />
            {state.goals.hasDebt && <DebtPlanner />}
            <Divider sx={{ my: 1, borderColor: '#EEF0F3' }} />
            <SaveExportBar />
            <TransitionCTA />
          </Box>
        )}
      </Container>
    </>
  );
}

export default function BudgetPlannerPage() {
  return (
    <BudgetProvider>
      <BudgetPlannerContent />
    </BudgetProvider>
  );
}
